import { faCheck, faCopy, faDownload, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useRef, useState } from 'react'
import { useMfaConfirm, useMfaSetup } from '../api/useMfa'
import { MfaEnrolment as Enrolment } from '../types'

// A single input, not react-otp-input-hook's per-digit boxes: its register()
// calls useCallback internally, so it is a hook. This component returns early
// while the secret loads and again once recovery codes arrive, so register would
// run zero times on some renders and six on others -- a changing hook count,
// which React rejects with error #310 and unmounts the entire tree.

/**
 * Displays a set of recovery codes. Exported because the page also shows it after
 * regenerating, and these are only ever displayed once.
 */
export const RecoveryCodes = ({ codes, username }: { codes: string[]; username?: string }) => {
    const [copied, setCopied] = useState(false)

    const asText = codes.join('\n')

    const download = () => {
        // A Blob URL rather than a data: URI so the filename is controllable and
        // the browser treats it as a genuine download.
        const blob = new Blob([`BR1 two-factor recovery codes\n\n${asText}\n`], { type: 'text/plain' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `br1-recovery-codes${username ? `-${username}` : ''}.txt`
        link.click()
        URL.revokeObjectURL(url)
    }

    const copy = async () => {
        await navigator.clipboard.writeText(asText)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    return (
        <section className='flex flex-col items-center gap-4 w-full'>
            <div className='bg-yellow-500/20 border border-yellow-500 text-yellow-200 p-4 text-base font-normal text-center'>
                <strong className='block text-lg mb-1'>Save these codes now. This is the only time you will see them.</strong>
                Copy them, download them, or write them down and keep them somewhere safe. Each code works once, and
                they are the only way back into your account if you lose your phone.
            </div>

            <ul className='grid grid-cols-1 sm:grid-cols-2 gap-2 w-full font-mono text-lg'>
                {codes.map((code) => (
                    <li key={code} className='bg-black/50 border p-2 text-center tracking-widest'>
                        {code}
                    </li>
                ))}
            </ul>

            <div className='flex flex-row gap-2 w-full'>
                <button
                    type='button'
                    onClick={download}
                    className='flex-1 flex flex-row gap-2 items-center justify-center p-3 border hover:outline text-base'
                >
                    <FontAwesomeIcon icon={faDownload} />
                    <span>Download</span>
                </button>
                <button
                    type='button'
                    onClick={copy}
                    className='flex-1 flex flex-row gap-2 items-center justify-center p-3 border hover:outline text-base'
                >
                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                    <span>{copied ? 'Copied' : 'Copy'}</span>
                </button>
            </div>
        </section>
    )
}

/**
 * Enrol an authenticator app.
 *
 * The QR is rendered server-side and delivered as a data: URI, so this needs no
 * QR library -- the frontend image is built with `yarn install --frozen-lockfile`,
 * and adding a dependency for a security feature would mean regenerating the
 * lockfile as part of the same change.
 */
export const MfaEnrolment = ({
    username,
    onComplete,
    onStarted,
}: {
    username?: string
    onComplete?: () => void
    /**
     * Fired once the secret has been requested. The parent uses this to pin this
     * component in place: without it, a status refetch mid-enrolment can swap the
     * parent to a different branch and unmount this component along with the
     * recovery codes, which are displayed exactly once.
     */
    onStarted?: () => void
}) => {
    const [otp, setOtp] = useState('')
    const [showSecret, setShowSecret] = useState(false)
    const [enrolment, setEnrolment] = useState<Enrolment | null>(null)
    const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)

    const setup = useMfaSetup()
    const confirm = useMfaConfirm()

    // Mint the secret exactly once. Every call replaces the pending secret
    // server-side, so a second invocation would leave the user scanning a QR the
    // server has already discarded, and confirmation would then fail with a valid
    // code. The ref guards against StrictMode's double-invoked effects should
    // anyone enable it later.
    const requested = useRef(false)
    useEffect(() => {
        if (requested.current) return
        requested.current = true
        onStarted?.()
        setup.mutate(undefined, { onSuccess: setEnrolment })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const submit = (e) => {
        e.preventDefault()
        if (otp.length < 6) return
        confirm.mutate(
            { code: otp },
            {
                onSuccess: (data) => {
                    setRecoveryCodes(data.recovery_codes)
                },
            }
        )
    }

    if (recoveryCodes) {
        return (
            <section className='flex flex-col items-center gap-6 w-full max-w-xl mx-auto'>
                <header className='text-2xl uppercase font-black text-center'>Two-factor is on</header>
                <RecoveryCodes codes={recoveryCodes} username={username} />
                {onComplete && (
                    <button
                        type='button'
                        onClick={onComplete}
                        className='p-4 w-full md:w-1/2 hover:outline'
                        style={{ background: 'linear-gradient(142deg,#03CEFF 0%,#C91FFF 100%)' }}
                    >
                        I have saved my codes
                    </button>
                )}
            </section>
        )
    }

    if (setup.isLoading || (!enrolment && !setup.error)) {
        return (
            <div className='flex flex-row items-center justify-center gap-2 p-8'>
                <FontAwesomeIcon icon={faSpinner} spin />
                <span className='text-base font-normal'>Preparing your enrolment…</span>
            </div>
        )
    }

    if (setup.error) {
        return (
            <div className='flex flex-col items-center gap-4 p-8'>
                <label className='login-form error text-center'>{setup.error.message}</label>
                <button type='button' className='underline text-base' onClick={() => setup.mutate()}>
                    Try again
                </button>
            </div>
        )
    }

    return (
        <section className='flex flex-col items-center gap-6 w-full max-w-xl mx-auto'>
            <header className='text-2xl uppercase font-black text-center'>Set up two-factor authentication</header>

            <p className='text-base font-normal text-center'>
                Staff accounts can approve withdrawals and grant balances, so they need a second factor. Scan this code
                with an authenticator app such as Google Authenticator, Authy, or 1Password.
            </p>

            <img
                src={enrolment.qr_data_uri}
                alt='Two-factor enrolment QR code'
                className='bg-white p-4 w-56 h-56 object-contain'
            />

            <button type='button' className='text-base font-normal underline' onClick={() => setShowSecret((v) => !v)}>
                {showSecret ? 'Hide setup key' : "Can't scan? Enter a key manually"}
            </button>

            {showSecret && (
                <code className='bg-black/50 border p-3 font-mono tracking-widest break-all text-center text-base'>
                    {enrolment.secret}
                </code>
            )}

            <form className='flex flex-col items-center gap-4 w-full' onSubmit={submit}>
                {/* tracking adds a letter-space after the final digit too, so
                    indent-[0.25em] compensates and keeps the digits centred. */}
                <p className='text-base font-normal text-center'>
                    Enter the 6-digit code from the app to finish.
                </p>

                <input
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder='000000'
                    inputMode='numeric'
                    autoComplete='one-time-code'
                    maxLength={6}
                    className='bg-black/50 backdrop-blur-2xl border text-white text-center text-2xl md:text-3xl tracking-[0.25em] indent-[0.25em] w-full max-w-xs p-3'
                />

                <label className='login-form error text-center'>{confirm.error?.message}</label>

                <button
                    type='submit'
                    disabled={confirm.isLoading || otp.length < 6}
                    className='flex flex-row gap-2 items-center justify-center p-4 w-full md:w-1/2 hover:outline disabled:opacity-50'
                    style={{ background: 'linear-gradient(142deg,#03CEFF 0%,#C91FFF 100%)' }}
                >
                    <span>Confirm</span>
                    {confirm.isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
                </button>
            </form>
        </section>
    )
}
